/**
 * src/components/games/CyberChessActivity.tsx
 *
 * Voice-channel multiplayer Chess. v1 scope:
 *   - Two-player (white/black). Spectators see the board but can't move.
 *   - Host-authoritative. The host keeps one chess.js instance, applies
 *     moves locally, broadcasts the resulting FEN + clocks + lastMove via
 *     GameStateSync.chessState. Peers render from the broadcast.
 *   - Peer sends a `{from, to, promotion?}` payload to the host via
 *     `NetworkManager.sendChessMove` whenever they click a piece. Host
 *     applies + re-broadcasts. The peer also applies the move locally
 *     for instant visual feedback; the host-broadcast echo lands later
 *     and a controlled-FEN sync re-aligns (no-op when already current).
 *   - Color allocation comes from the server's NEW `role-assignment`
 *     socket event (added in this turn) instead of the legacy
 *     `host-assignment: boolean` — fixing the long-standing "both sides
 *     spawn as white" race condition.
 *
 * Out of v1 scope (deferred to v2):
 *   - Board-flip orientation per player color (both peers currently see
 *     the board from white's perspective — chess convention but a UX
 *     polish opportunity).
 *   - Rematch / Surrender buttons (re-using FreeDoom's lifecycle ping
 *     pattern; requires server-side move-cancellation flow).
 *   - Reconnect-with-same-color reassignment (would need a `color`
 *     socket property in addition to `id`).
 *
 * Self-registers with GamesRegistry on module load. The framework's
 * `buildGameEntries()` already filters the LEGACY `id: 'chess'`
 * CyberChessGame IframeGame-free entry from the dispatch list once
 * this manifest lands, so the voice-channel activity supersedes the
 * standalone CyberChess source automatically — no GameManager.tsx-side
 * wiring beyond the new import + STATIC_COMPONENTS slot.
 */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { Chess, Move } from 'chess.js';
import {
  Shield,
  Wifi,
  WifiOff,
  X,
  RefreshCw,
  Crown,
  Trophy,
  Skull,
  Users,
  Cpu,
  Clock,
} from 'lucide-react';
import type { GameRuntimeProps } from '../../game/framework/types';
import { registerGame } from '../../game/framework/GamesRegistry';
import { NetworkManager } from '../../game/NetworkManager';
import ChessBoard, { ChessBoardRef } from '../../game/CyberChess/src/components/ChessBoard';
import { recordChessResult } from '../../services/leaderboardService';
import { getBestMove, Difficulty } from '../../game/CyberChess/src/services/aiService';

// ============================================================================
// Sync shape — host broadcasts to peers via state-sync.payload.chessState
// ============================================================================

interface ChessSyncState {
  fen: string;
  turn: 'w' | 'b';
  whiteTime: number;     // remaining seconds
  blackTime: number;     // remaining seconds
  isTimerRunning: boolean;
  gameOver: string | null;
  hostUserId: string;
  lastMove: { from: string; to: string; promotion?: string } | null;
}

const INITIAL_CLOCK_SECONDS = 300;
const TICK_MS = 1000;

function emptySyncState(hostUserId: string): ChessSyncState {
  return {
    fen: new Chess().fen(),
    turn: 'w',
    whiteTime: INITIAL_CLOCK_SECONDS,
    blackTime: INITIAL_CLOCK_SECONDS,
    isTimerRunning: false,
    gameOver: null,
    hostUserId,
    lastMove: null,
  };
}

/**
 * Derive a "GameOver" string from a chess.js instance, mirroring the
 * wording in the original CyberChessGame but condensed for the activity
 * HUD. Returns null if the position is not game-over.
 */
function deriveGameOverReason(chess: Chess): string | null {
  if (chess.isCheckmate()) {
    // chess.js: `turn()` returns the side to move. On checkmate, that's
    // the side that just LOST.
    const loser = chess.turn();
    const winner = loser === 'w' ? 'Black' : 'White';
    return `Checkmate · ${winner} wins`;
  }
  if (chess.isThreefoldRepetition()) return 'Threefold repetition';
  if (chess.isInsufficientMaterial()) return 'Insufficient material';
  if (chess.isStalemate()) return 'Stalemate';
  if (chess.isDraw()) return 'Draw';
  return null;
}

// ============================================================================
// CyberChessActivity component
// ============================================================================

export default function CyberChessActivity(props: GameRuntimeProps) {
  const { session, playerSelf, onExit, onChange, notify, mode = 'multiplayer', initialRoomId } = props;

  // --------------------------------------------------------------------------
  // Refs that don't trigger re-renders but need to be readable from inside
  // event handlers (state captures from closures go stale on hot paths).
  // --------------------------------------------------------------------------
  const boardRef = useRef<ChessBoardRef | null>(null);
  const networkRef = useRef<NetworkManager | null>(null);
  const isHostRef = useRef<boolean>(false);
  /**
   * Marker for "the next `onMove` event from ChessBoard is the echo of a
   * peer-applied imperative `boardRef.makeMove(...)` call — do NOT
   * re-broadcast it from this client". Set in onPlayerUpdate right
   * before applying the imperative move; cleared on the matching onMove.
   * Prevents the loop where a host receives a peer's move → applies →
   * onMove fires → host treats it like its own click → re-sends to peer.
   */
  const pendingImperativeRef = useRef<{
    from: string;
    to: string;
    promotion?: string;
  } | null>(null);
  /**
   * Latched game-over marker. The same game-over event must fire the
   * record_chess_result RPC at most once per React render cycle AND
   * across both peers (the RPC's `last_match_key` idempotency is the
   * server-side safety net; this ref prevents the client from
   * double-firing on a quick SyncState re-emission). Cleared on
   * Restart so a fresh game can record its own outcome.
   */
  const lastRecordedGameOverRef = useRef<string | null>(null);

  // --------------------------------------------------------------------------
  // React state
  // --------------------------------------------------------------------------
  const [syncState, setSyncState] = useState<ChessSyncState | null>(null);
  const [isHost, setIsHost] = useState<boolean>(false);
  const [myColor, setMyColor] = useState<'w' | 'b' | 'spectator'>('spectator');
  const [connectionStatus, setConnectionStatus] = useState<
    'connecting' | 'connected' | 'disconnected'
  >('connecting');
  /** Inactivity of the last application of a peer move, used to flash a toast. */
  const [lastAppliedPeerMoveAt, setLastAppliedPeerMoveAt] = useState<number | null>(
    null
  );
  /** Single-player AI difficulty (ROOKIE/VETERAN/MASTER). Only used in solo mode. */
  const [aiDifficulty, setAiDifficulty] = useState<Difficulty>('VETERAN');
  /** True while the AI is computing its move. Used to gate the trigger effect re-entry. */
  const [isAiThinking, setIsAiThinking] = useState<boolean>(false);

  const myUserId: string | null = playerSelf?.userId ?? null;

  // --------------------------------------------------------------------------
  // Lifecycle ping (mount → 'chess', unmount → null) — mirrors SnakeActivity
  // so the user's own 🎮 pill lights up on every enter path (rail launch,
  // NetworkManager round-trip, hydration edge cases), not just rail navigation.
  // --------------------------------------------------------------------------
  useEffect(() => {
    // Both modes report 'chess' — there is one manifest for the game now,
    // with solo/multiplayer chosen at launch (the 'chess-solo' GameId is
    // gone along with its duplicate registry entry).
    onChange?.('chess');
    return () => onChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------------------------------------------------------
  // Single-player initializer. When mode === 'single-player' the activity
  // has no session.id so the NetworkManager setup below skip-runs by
  // happening to early-return on missing roomId. We seed a local syncState
  // here so the board renders with the local user as host + white and the
  // rest of the activity (clocks, last-move panel, AI trigger effect, etc.)
  // has something to read from instead of staying in the connecting state.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'single-player') return;
    if (syncState !== null) return;
    if (!myUserId) return;
    setIsHost(true);
    // Solo mode never spins up a NetworkManager, so isHostRef (the ref
    // that handleRestart/handleBoardGameOver actually gate on) would
    // otherwise stay false forever — only nm.onHostAssignment sets it,
    // and that callback never fires without a network connection.
    isHostRef.current = true;
    setMyColor('w');
    setConnectionStatus('connected');
    setSyncState(emptySyncState(myUserId));
  }, [mode, myUserId, syncState]);

  // --------------------------------------------------------------------------
  // NetworkManager lifecycle. Re-mounts when the voice-channel session id
  // changes. The constructor emits `join-vault`; the server replies with
  // both `host-assignment` (legacy boolean — kept for UwU/Snake compat) and
  // `role-assignment` (NEW — gives us a deterministic color so 'both sides
  // white' can't recur).
  // --------------------------------------------------------------------------
  useEffect(() => {
    // Explicit guard (not just relying on roomId staying undefined) —
    // CyberChessSoloLauncher doesn't forward initialRoomId today, but this
    // is exactly the class of bug just fixed in DrawTogether's solo mode
    // (an accidental room id silently opening a real network connection
    // in what's supposed to be an isolated solo session).
    if (mode === 'single-player') return;
    // session?.id is always undefined in practice today — nothing builds
    // a real GameSession (see the GameRuntimeProps comment on
    // initialRoomId) — so fall back to the voice-channel id GameManager
    // is mounted with. This restores the join-vault/host-assignment/
    // role-assignment handshake below; it does NOT restore session.players
    // (used further down for peer-id resolution), which still has no
    // real data source and continues to degrade gracefully.
    const roomId = session?.id ?? initialRoomId;
    if (!roomId) return;

    const nm = new NetworkManager(roomId, 'chess');
    networkRef.current = nm;

    nm.onHostAssignment = (assignedHost) => {
      isHostRef.current = assignedHost;
      setIsHost(assignedHost);
      setConnectionStatus('connected');
    };

    nm.onRoleAssignment = (color, reason) => {
      setMyColor(color);
      setConnectionStatus('connected');
      // Visible UI feedback when a slot frees up: black left → white is now
      // a player with no opponent. We surface the toast so the white peer
      // knows the next click goes nowhere useful.
      if (reason === 'black-disconnected') {
        notify?.('Your opponent left the game.', { kind: 'warn' });
      } else if (reason === 'white-disconnected') {
        notify?.('White disconnected — vault will close.', { kind: 'warn' });
      } else if (color === 'spectator' && reason === 'two-player-full') {
        notify?.('Two-player game is full; spectating until someone leaves.', {
          kind: 'info',
        });
      }
    };

    nm.onStateSync = (raw) => {
      const chessState = (raw as any).chessState as ChessSyncState | undefined;
      if (chessState && typeof chessState === 'object') {
        setSyncState(chessState);
        setConnectionStatus('connected');
      }
    };

    /**
     * Incoming peer move (the chessMove-shaped payload from `sendChessMove`).
     * Host-only path: apply via imperative ref, mark for echo suppression,
     * and re-broadcast so the peer round-trips in <250ms instead of waiting
     * for the next clock tick to trickle out a state-sync.
     *
     * Spectators also receive this onPlayerUpdate event in v2 (for an
     * observer-overlay UI); v1 silently drops peer moves for spectators —
     * their board still re-renders from state-sync.
     */
    nm.onPlayerUpdate = (data) => {
      if (!isHostRef.current) return;
      const cm = (data as any)?.chessMove as
        | { from: string; to: string; promotion?: string }
        | undefined;
      if (!cm || !cm.from || !cm.to) return;
      pendingImperativeRef.current = cm;
      const applied = boardRef.current?.makeMove(cm) ?? false;
      if (!applied) {
        // Illegal move from a peer (desync, illegal click). Don't echo.
        pendingImperativeRef.current = null;
        return;
      }
      const fen = boardRef.current?.getFen() ?? '';
      const turn = boardRef.current?.getTurn() ?? 'w';
      setLastAppliedPeerMoveAt(Date.now());
      // Broadcast the post-move state immediately so the peer sees their
      // click confirmed without waiting for the next clock tick.
      setSyncState((prev) => {
        const next: ChessSyncState = {
          ...(prev ?? emptySyncState(myUserId ?? '')),
          fen,
          turn,
          lastMove: cm,
          // First-ever move starts the clock.
          isTimerRunning: prev?.isTimerRunning ?? false,
          gameOver: deriveGameOverReason(new Chess(fen)),
          hostUserId: prev?.hostUserId ?? (myUserId ?? ''),
        };
        nm.sendState({
          aliens: [], bullets: [], score: 0, lives: 0, level: 0, wave: 0,
          state: 'chess',
          chessState: next,
        });
        return next;
      });
    };

    return () => {
      nm.disconnect();
      networkRef.current = null;
      isHostRef.current = false;
      setIsHost(false);
      setSyncState(null);
      setConnectionStatus('disconnected');
      pendingImperativeRef.current = null;
    };
  }, [mode, session?.id, initialRoomId, myUserId, notify]);

  // --------------------------------------------------------------------------
  // Host bootstrap: when the host connects and no sync state has arrived
  // yet, seed an initial FEN (the standard starting position) and broadcast.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isHost) return;
    if (syncState) return; // already seeded
    if (!session) return;
    const seeded: ChessSyncState = emptySyncState(session.hostUserId);
    setSyncState(seeded);
    networkRef.current?.sendState({
      aliens: [], bullets: [], score: 0, lives: 0, level: 0, wave: 0,
      state: 'chess',
      chessState: seeded,
    });
  }, [isHost, syncState, session]);

  // --------------------------------------------------------------------------
  // Host clock tick (1 Hz). Decrements the side-to-move's clock and
  // converts a 0 clock into a timeout game-over. Each tick also broadcasts
  // the new state so the peer's clocks stay in sync. Skipped while paused
  // (isTimerRunning=false) or while the game is over.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isHost || !syncState) return;
    if (!syncState.isTimerRunning) return;
    if (syncState.gameOver) return;
    const id = window.setInterval(() => {
      setSyncState((prev) => {
        if (!prev || prev.gameOver) return prev;
        const side = prev.turn;
        const next: ChessSyncState = { ...prev };
        if (side === 'w') {
          next.whiteTime = Math.max(0, prev.whiteTime - 1);
          if (next.whiteTime === 0) {
            next.gameOver = 'Timeout · Black wins';
            next.isTimerRunning = false;
          }
        } else {
          next.blackTime = Math.max(0, prev.blackTime - 1);
          if (next.blackTime === 0) {
            next.gameOver = 'Timeout · White wins';
            next.isTimerRunning = false;
          }
        }
        networkRef.current?.sendState({
          aliens: [], bullets: [], score: 0, lives: 0, level: 0, wave: 0,
          state: 'chess',
          chessState: next,
        });
        return next;
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [isHost, syncState?.isTimerRunning, syncState?.turn, syncState?.gameOver]);

  // --------------------------------------------------------------------------
  // Single-player AI move trigger. Mirrors the legacy CyberChessGame AI
  // loop: every render cycle checks if it's black's turn; if so, kicks
  // off a 600ms-delayed getBestMove call so the player's piece-lift
  // animation has time to settle before the AI applies its move
  // imperatively via `boardRef.current?.makeMove(...)`. ChessBoard's
  // onMove callback fires for the imperative apply, but the
  // pendingImperativeRef marker (set right before the apply) makes
  // handleBoardMove swallow the echo without bouncing it through any
  // network path.
  //
  // Race safety: the effect runs synchronously and returns a cleanup
  // callback that:
  //   - flips a `cancelled` flag so any in-flight AI resolve is swallowed
  //   - clears the setTimeout so a stale callback can't apply after
  //     the FEN has changed underneath it
  // The imperative makeMove falls back gracefully on illegal moves
  // (returns false) so a buggy AI service can't crash the UI.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (mode !== 'single-player') return;
    if (!syncState) return;
    if (syncState.gameOver) return;
    if (syncState.turn !== 'b') return;

    let cancelled = false;
    setIsAiThinking(true);
    const fen = syncState.fen;

    const timer = setTimeout(async () => {
      const aiMove = await getBestMove(fen, aiDifficulty);
      // `cancelled` guards a stale resolved getBestMove promise from
      // applying after a newer effect run cancelled this one. Note
      // `isAiThinking` is intentionally NOT in this effect's dependency
      // array (nor a re-entrancy guard at the top) — it exists purely to
      // drive the "AI is calculating" UI indicator; using it as a guard
      // previously caused the effect to self-cancel before the timer
      // could ever fire (setting it true re-triggered the effect, which
      // then immediately bailed out on its own just-set value).
      if (cancelled) {
        return;
      }
      if (!aiMove) {
        setIsAiThinking(false);
        return;
      }
      pendingImperativeRef.current = {
        from: aiMove.from,
        to: aiMove.to,
        ...(aiMove.promotion ? { promotion: aiMove.promotion } : {}),
      };
      const applied = boardRef.current?.makeMove(aiMove) ?? false;
      if (!applied) {
        pendingImperativeRef.current = null;
        setIsAiThinking(false);
        // The AI picked a move that is illegal in the board's CURRENT
        // position, which means syncState had drifted from ChessBoard's
        // internal game and we computed against a stale FEN. Bailing here
        // used to be terminal: this effect keys on syncState.fen/turn, so
        // if we left the stale values in place nothing would ever change
        // and the AI would never be asked again — the opponent just froze
        // for the rest of the match. Re-seed from the board's authoritative
        // FEN instead, which both repairs the drift and re-triggers this
        // effect if it is still the AI's turn.
        const liveFen = boardRef.current?.getFen();
        if (liveFen) {
          const live = new Chess(liveFen);
          setSyncState(prev => (prev && prev.fen !== liveFen
            ? { ...prev, fen: liveFen, turn: live.turn(), gameOver: deriveGameOverReason(live) }
            : prev));
        }
        return;
      }
      const chessNow = new Chess(boardRef.current?.getFen() ?? fen);
      setSyncState((prev) => {
        if (!prev) return prev;
        const next: ChessSyncState = {
          ...prev,
          fen: chessNow.fen(),
          turn: chessNow.turn(),
          lastMove: {
            from: aiMove.from,
            to: aiMove.to,
            ...(aiMove.promotion ? { promotion: aiMove.promotion } : {}),
          },
          gameOver: deriveGameOverReason(chessNow),
        };
        return next;
      });
      pendingImperativeRef.current = null;
      setIsAiThinking(false);
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      // Reset synchronously here rather than relying on the setTimeout
      // callback to do it — clearTimeout prevents that callback from ever
      // running, so if isAiThinking weren't reset in cleanup too, it would
      // stay stuck `true` forever after any cancellation (e.g. the user
      // changes aiDifficulty mid-think), permanently freezing the AI turn.
      setIsAiThinking(false);
    };
  }, [mode, syncState?.turn, syncState?.gameOver, syncState?.fen, aiDifficulty]);
  // compute the same matchKey (`${session.id}::${gameOver}`), so the DB-
  // level `last_match_key` idempotency in record_chess_result is the safety
  // net against either peer's RPC double-call. The lastRecordedGameOverRef
  // latches per-client so a SyncState re-emit doesn't double-fire on the
  // same React render.
  //
  // Solo bypass: single-player matches don't write to the leaderboards
  // table. Reasons: (a) recordChessResult requires a real UUID for
  // blackUserId (FK constraint on profiles.id), and there is no AI
  // profile row to satisfy it; (b) recording ROOKIE AI wins would let
  // players artificially farm leaderboard rankings.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (mode === 'single-player') return;  // solo matches don't write leaderboard rows (FK + fairness)
    const gameOver = syncState?.gameOver;
    // Game-over → leaderboard write. Fires once per match outcome. Both peers
    if (!gameOver) return;
    if (!session?.id) return;
    if (!myUserId) return;
    if (myColor === 'spectator') return; // spectators don't record outcomes

    // Latch: same gameOver string → no re-fire from this client.
    if (lastRecordedGameOverRef.current === gameOver) return;

    // Resolve both peer user IDs from session.players. The framework
    // populates GameSession.players with {userId, color?, ...} entries
    // for voice-channel games; for chess these have color=='w'/'b'. If
    // the roster isn't populated yet we log a single warning and back
    // off — better than crashing the chess UI for a missing roster.
    const peers = Object.values(session.players ?? {});
    const whiteEntry = peers.find((p) => (p.color ?? null) === 'w');
    const blackEntry = peers.find((p) => (p.color ?? null) === 'b');
    if (!whiteEntry || !blackEntry) {
      // One-shot warning; the gate itself short-circuits all subsequent
      // passes because once we set lastRecordedGameOverRef below this
      // branch won't return early again for the same gameOver value.
      // We deliberately do NOT set the ref here so a future framework
      // pass that DOES populate session.players can retry.
      console.warn(
        '[CyberChessActivity] record_chess_result skipped: session.players ' +
        'lacks both white + black entries. GameActivityManager should ' +
        'populate GameSession.players with role-assigned entries.',
      );
      return;
    }
    const whiteUserId = whiteEntry.userId;
    const blackUserId = blackEntry.userId;
    if (!whiteUserId || !blackUserId) return;

    // Derive outcome from the gameOver string. The wording is owned by
    // deriveGameOverReason() above; an unrecognized shape (e.g., a
    // server-declared abort) falls through to "skip" rather than
    // writing a malformed row.
    let isDraw = false;
    let winnerUserId: string | null = null;
    if (/Stalemate|Threefold|Insufficient|^Draw\b/i.test(gameOver)) {
      isDraw = true;
    } else if (gameOver.includes('Black')) {
      winnerUserId = blackUserId;
    } else if (gameOver.includes('White')) {
      winnerUserId = whiteUserId;
    } else {
      return; // unknown gameOver shape
    }

    // Latch BEFORE the async fire so a churn re-render can't double-call.
    lastRecordedGameOverRef.current = gameOver;

    const matchKey = `${session.id}::${gameOver}`;
    recordChessResult({
      whiteUserId,
      blackUserId,
      winnerUserId,
      isDraw,
      matchKey,
    }).catch((err) => {
      // Best-effort: a transient RPC failure should never crash the
      // chess UI. Log + clear the local latch so a Restart chain isn't
      // permanently blocked. Server-side idempotency keeps repeat
      // calls from mutating counter columns twice.
      console.warn('[CyberChessActivity] record_chess_result failed:', err);
      lastRecordedGameOverRef.current = null;
    });
  }, [
    syncState?.gameOver,
    session?.id,
    session?.players,
    myColor,
    myUserId,
  ]);

  // --------------------------------------------------------------------------
  // Restart — clears the recorded-game-over latch so the new round can
  // record its own outcome. (Without this we'd skip the next game's
  // recordChessResult because the latch would still point at the previous
  // gameOver string, which won't repeat — defensive cleanup anyway.)
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (syncState?.gameOver === null) {
      lastRecordedGameOverRef.current = null;
    }
  }, [syncState?.gameOver]);

  // --------------------------------------------------------------------------
  // ChessBoard's onMove callback. Two paths:
  //   1. Echo of an imperative `boardRef.makeMove(...)` we just applied
  //      from a peer; clear `pendingImperativeRef` and skip the broadcast.
  //   2. Real local click. Host: broadcast. Peer: send to host via
  //      `sendChessMove`.
  // --------------------------------------------------------------------------
  const handleBoardMove = useCallback((move: Move) => {
    // Spectator gate — a third joiner whose role-assignment came back
    // as 'spectator' can see the board but must not be able to inject
    // moves into the game. Without this, a stray click on a spectator's
    // browser would fire ChessBoard's internal makeMove → onMove here →
    // (non-host else branch) → sendChessMove → host applies + broadcasts.
    // ChessBoard also gets a `readOnly` prop so its click handlers
    // short-circuit before onSquareClick ever fires; this gate is the
    // belt-and-braces firewall inside the React handler layer.
    if (myColor === 'spectator') return;
    const pending = pendingImperativeRef.current;
    if (
      pending &&
      pending.from === move.from &&
      pending.to === move.to &&
      (pending.promotion ?? null) === (move.promotion ?? null)
    ) {
      pendingImperativeRef.current = null;
      return;
    }
    if (mode === 'single-player') {
      // Local click path. The player is white; if it's not white's turn
      // an out-of-order click somehow leaked through (ChessBoard is
      // turn-aware but defends the React handler too). Mirror the move
      // into syncState from boardRef's authoritative post-move FEN so we
      // never diverge from the ChessBoard internal state.
      const chessNow = new Chess(boardRef.current?.getFen() ?? '');
      setSyncState((prev) => {
        if (!prev) return prev;
        if (prev.turn !== 'w') return prev;
        const next: ChessSyncState = {
          ...prev,
          fen: chessNow.fen(),
          turn: chessNow.turn(),
          lastMove: {
            from: move.from,
            to: move.to,
            ...(move.promotion ? { promotion: move.promotion } : {}),
          },
          // First-ever move starts the clock.
          isTimerRunning: prev.isTimerRunning || prev.lastMove === null,
          gameOver: deriveGameOverReason(chessNow),
        };
        return next;
      });
      return;
    }
    if (isHostRef.current) {
      // Host just clicked. Update local state immediately (don't wait
      // for the chess-derived state-sync round-trip) and emit broadcast.
      const chessNow = boardRef.current
        ? new Chess(boardRef.current.getFen())
        : null;
      setSyncState((prev) => {
        const base = prev ?? emptySyncState(myUserId ?? '');
        if (!chessNow) return base;
        const next: ChessSyncState = {
          ...base,
          fen: chessNow.fen(),
          turn: chessNow.turn(),
          lastMove: {
            from: move.from,
            to: move.to,
            promotion: move.promotion,
          },
          isTimerRunning: base.isTimerRunning || (move.from !== undefined && base.lastMove === null),
          gameOver: deriveGameOverReason(chessNow),
        };
        // First-ever move starts the clock.
        networkRef.current?.sendState({
          aliens: [], bullets: [], score: 0, lives: 0, level: 0, wave: 0,
          state: 'chess',
          chessState: next,
        });
        return next;
      });
    } else {
      // Peer clicks. The local board already applied the move (ChessBoard
      // is interactive in both modes for instant feedback); we don't echo
      // ourselves, just send the {from,to,promotion} payload to the host.
      const cm: { from: string; to: string; promotion?: string } = {
        from: move.from,
        to: move.to,
      };
      if (move.promotion) cm.promotion = move.promotion;
      networkRef.current?.sendChessMove(cm);
    }
  }, [mode, myUserId]);

  // --------------------------------------------------------------------------
  // Game-over banner detection from ChessBoard's onGameOver callback. We
  // trust chess.js's view of the position because the host's local chess
  // instance is the source of truth. (Freeze and broadcast the reason.)
  // --------------------------------------------------------------------------
  // --------------------------------------------------------------------------
  const handleBoardGameOver = useCallback((result: string) => {
    if (!isHostRef.current) return;
    setSyncState((prev) => {
      const next: ChessSyncState = {
        ...(prev ?? emptySyncState(myUserId ?? '')),
        gameOver: result,
        isTimerRunning: false,
      };
      networkRef.current?.sendState({
        aliens: [], bullets: [], score: 0, lives: 0, level: 0, wave: 0,
        state: 'chess',
        chessState: next,
      });
      return next;
    });
  }, [myUserId]);

  // --------------------------------------------------------------------------
  // Restart — host only. Reset chess.js instance, clocks, gameOver field,
  // lastMove; re-seed the broadcast.
  // --------------------------------------------------------------------------
  const handleRestart = useCallback(() => {
    if (!isHostRef.current) {
      notify?.('Only the host can restart the round.', { kind: 'warn' });
      return;
    }
    const next: ChessSyncState = {
      ...emptySyncState(myUserId ?? ''),
      hostUserId: myUserId ?? '',
    };
    setSyncState(next);
    networkRef.current?.sendState({
      aliens: [], bullets: [], score: 0, lives: 0, level: 0, wave: 0,
      state: 'chess',
      chessState: next,
    });
    notify?.('Round restarted.', { kind: 'info' });
  }, [myUserId, notify]);

  const handleExit = useCallback(() => {
    onChange?.(null);
    onExit();
  }, [onChange, onExit]);

  // --------------------------------------------------------------------------
  // Derived view state
  // --------------------------------------------------------------------------
  const isMyTurn = useMemo(
    () => myColor !== 'spectator' && syncState?.turn === myColor && !syncState?.gameOver,
    [myColor, syncState?.turn, syncState?.gameOver]
  );
  const isSpectator = myColor === 'spectator';

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <div className="relative w-full h-full bg-[#040711] text-white flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-gradient-to-r from-black via-black to-[#0a0612]">
        <div className="flex items-center gap-3">
          <Shield className="text-[var(--neon)]" size={26} />
          <div className="flex flex-col">
            <h1 className="text-lg font-black tracking-tight">
              CyberChess{' '}
              <span className="text-[10px] text-white/30 font-mono uppercase ml-1">
                {mode === 'single-player' ? 'single-player vs CyberChess AI' : 'voice-channel multiplayer'}
              </span>
            </h1>
            <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest flex items-center gap-3">
              <span className="flex items-center gap-1">
                {connectionStatus === 'connected' ? (
                  <Wifi size={10} className="text-emerald-400" />
                ) : connectionStatus === 'connecting' ? (
                  <Wifi size={10} className="text-amber-400 animate-pulse" />
                ) : (
                  <WifiOff size={10} className="text-red-400" />
                )}
                {connectionStatus}
              </span>
              {isHost && (
                <span className="flex items-center gap-1 text-[var(--neon)]">
                  <Crown size={10} /> host
                </span>
              )}
              <span
                className={`flex items-center gap-1 ${
                  isSpectator ? 'text-fuchsia-300' : 'text-white/40'
                }`}
                title={`Your assigned role from server role-assignment event`}
              >
                <Users size={10} />
                {isSpectator ? 'spectator' : myColor === 'w' ? 'white' : 'black'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'single-player' && syncState && (
            <select
              value={aiDifficulty}
              onChange={e => setAiDifficulty(e.target.value as Difficulty)}
              className="bg-white/5 border border-white/10 rounded-lg text-[10px] font-bold uppercase tracking-widest text-white/70 outline-none px-2 py-1.5 hover:border-white/30 transition-colors"
              title="AI difficulty"
              aria-label="CyberChess AI difficulty"
            >
              <option value="ROOKIE">Rookie AI</option>
              <option value="VETERAN">Veteran AI</option>
              <option value="MASTER">Master AI</option>
            </select>
          )}
          {isAiThinking && mode === 'single-player' && (
            <span className="px-2 py-1.5 text-[10px] font-black uppercase tracking-widest text-fuchsia-300/80 flex items-center gap-1.5" title="AI is calculating">
              <Cpu size={11} className="animate-pulse" /> AI
            </span>
          )}
          {isHost && syncState && (
            <button
              onClick={handleRestart}
              className="px-3 py-1.5 rounded-xl border border-[var(--neon)]/40 text-[var(--neon)] text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--neon)]/10 transition-colors flex items-center gap-1.5"
              title="Restart (host only)"
            >
              <RefreshCw size={11} /> Restart
            </button>
          )}
          <button
            onClick={handleExit}
            className="px-3 py-1.5 rounded-xl border border-red-500/30 text-red-400 text-[10px] font-bold uppercase tracking-widest hover:bg-red-500/15 transition-colors flex items-center gap-1.5"
          >
            <X size={11} /> Exit
          </button>
        </div>
      </div>

      {/* Main: board + side panel */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        <div className="flex-1 flex items-center justify-center p-4 bg-[#020308] relative">
          {syncState ? (
            <ChessBoard
              ref={boardRef}
              onMove={handleBoardMove}
              onGameOver={handleBoardGameOver}
              isAiThinking={isAiThinking && mode === 'single-player'}
              controlledFen={syncState.fen}
              // Was `isSpectator` alone, which in solo mode is always false —
              // so the board stayed live on BLACK's turn and the player could
              // move the AI's pieces (ChessBoard only checks that the clicked
              // piece matches the side to move, not that it's *your* side).
              // Gating on isMyTurn locks the board whenever it isn't your
              // turn, in both solo and multiplayer.
              readOnly={isSpectator || !isMyTurn}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 text-white/40 font-mono text-xs">
              <Clock className="animate-pulse" size={28} />
              <span>
                {mode === 'single-player'
                  ? 'Initializing AI opponent\u2026'
                  : 'Waiting for host to seed the board\u2026'}
              </span>
            </div>
          )}
          {isSpectator && syncState && (
            <div className="absolute top-3 left-3 right-3 bg-amber-500/10 border border-amber-500/40 rounded-xl px-3 py-2 text-[11px] text-amber-200">
              Spectating. The two-player game is full — open a new vault or
              wait for one of the players to leave.
            </div>
          )}
          {!isHost && !isSpectator && syncState && !isMyTurn && !syncState.gameOver && (
            <div className="absolute top-3 left-3 right-3 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-[11px] text-white/60">
              Waiting for the other side to move…
            </div>
          )}
        </div>

        {/* Right: clocks + move history */}
        <aside className="w-full md:w-72 bg-black/40 border-l border-white/10 p-4 flex flex-col gap-4 overflow-y-auto">
          {/* Clocks */}
          <div className="grid grid-cols-2 gap-3">
            <div
              className={`bg-slate-950/60 p-3 rounded-xl border text-center relative overflow-hidden ${
                syncState?.turn === 'w'
                  ? 'border-cyan-500/40'
                  : 'border-white/5'
              }`}
            >
              <p className="text-[9px] text-cyan-300/70 uppercase tracking-widest mb-1">White</p>
              <p className="text-2xl font-black text-white tabular-nums">
                {syncState?.whiteTime ?? INITIAL_CLOCK_SECONDS}
              </p>
              {syncState?.turn === 'w' && syncState?.isTimerRunning && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-cyan-400 animate-pulse" />
              )}
            </div>
            <div
              className={`bg-slate-950/60 p-3 rounded-xl border text-center relative overflow-hidden ${
                syncState?.turn === 'b'
                  ? 'border-fuchsia-500/40'
                  : 'border-white/5'
              }`}
            >
              <p className="text-[9px] text-fuchsia-300/70 uppercase tracking-widest mb-1">Black</p>
              <p className="text-2xl font-black text-white tabular-nums">
                {syncState?.blackTime ?? INITIAL_CLOCK_SECONDS}
              </p>
              {syncState?.turn === 'b' && syncState?.isTimerRunning && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-fuchsia-500 animate-pulse" />
              )}
            </div>
          </div>

          {/* Last move */ }
          <div className="bg-white/5 rounded-xl border border-white/10 p-3">
            <div className="text-[9px] uppercase text-white/40 font-black tracking-widest mb-1">Last Move</div>
            <div className="text-[12px] font-mono font-bold text-white">
              {syncState?.lastMove
                ? `${syncState.lastMove.from} → ${syncState.lastMove.to}${
                    syncState.lastMove.promotion ? `=${syncState.lastMove.promotion}` : ''
                  }`
                : '— (waiting for first move)'}
            </div>
          </div>

          {/* Game over banner */}
          {syncState?.gameOver && (
            <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 flex items-start gap-2">
              <Trophy className="text-red-300 shrink-0 mt-0.5" size={14} />
              <div className="text-[12px] text-red-200 font-bold">{syncState.gameOver}</div>
            </div>
          )}

          {/* Footer hint */}
          <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest mt-auto">
            {isHost
              ? 'You are the host — Restart resets the round'
              : isSpectator
                ? 'Spectator mode — no moves accepted'
                : isMyTurn
                  ? 'Your turn — click a piece'
                  : 'Waiting for opponent…'}
          </div>
        </aside>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 font-mono uppercase tracking-widest flex items-center gap-2 bg-black/30">
        <span>CyberChess v1 — {mode === 'single-player' ? 'single-player vs AI' : 'voice-channel multiplayer'}</span>
        {lastAppliedPeerMoveAt && (
          <span className="ml-2 opacity-70">
            · last applied peer move at{' '}
            {new Date(lastAppliedPeerMoveAt).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Self-register: tells GamesRegistry that 'chess' is now a registered
// multiplayer activity. GameManager.buildGameEntries() filters out the
// LEGACY `id: 'chess'` CyberChessGame entry because its id now matches a
// registered entry, so the in-app version takes over automatically —
// alongside Snake and FreeDoom.
// ============================================================================
registerGame({
  id: 'chess',
  displayName: 'CyberChess',
  shortDescription:
    'Voice-channel multiplayer chess — host-authoritative board, role-assigned colors, 5-min clocks.',
  multiplayer: true,
  // Also playable solo vs the aiService Minimax engine. One manifest, two
  // modes — this replaced the separate 'chess-solo' registry entry that
  // made CyberChess appear twice in the arcade picker.
  solo: true,
  multiplayerShape: {
    maxPlayers: 2,
    transport: 'socket.io',
    requiresVoiceChannel: true,
    hostMigration: 'auto',
  },
  leaderboard: 'win_loss_draw',
  available: true,
  asset: async () => ({ default: CyberChessActivity }),
  icon: Shield,
});
